import './NotificationCenter.less';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Empty, Popover } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';
import {
  BellOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { formatDistanceToNow } from 'date-fns';
import { useAccess } from 'hooks/useAccess';
import {
  useMarkNotificationEventsSeen,
  useNotificationEvents,
} from 'hooks/notificationEvents';
import { NotificationAction } from 'types/notification';

const LABELS: Record<
  NotificationAction,
  { running: string; success: string; failure: string }
> = {
  CLUSTER_CONNECT: {
    running: 'Connecting',
    success: 'Connected',
    failure: 'Connect failed',
  },
  CLUSTER_UPGRADE: {
    running: 'Upgrading',
    success: 'Upgrade completed',
    failure: 'Upgrade failed',
  },
  CLUSTER_BACKUP: {
    running: 'Backing up',
    success: 'Backup completed',
    failure: 'Backup failed',
  },
};

const ago = (date: string) =>
  formatDistanceToNow(new Date(date), { addSuffix: true });

function NotificationCenter({
  className,
  placement,
}: Readonly<{ className: string; placement: TooltipPlacement }>) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { can } = useAccess();
  const { data } = useNotificationEvents();
  const { mutate: markSeen } = useMarkNotificationEventsSeen();

  const running = data?.data.running ?? [];
  const events = data?.data.events ?? [];
  const unread = data?.data.unread ?? 0;

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next && unread > 0 && events.length) markSeen(events[0].completedAt);
  }

  const content =
    running.length || events.length ? (
      <ul className="NotificationCenter__list">
        {running.map(action => (
          <li key={action.id} className="NotificationCenter__item">
            <LoadingOutlined className="NotificationCenter__icon NotificationCenter__icon--running" />
            <div className="NotificationCenter__body">
              <div className="NotificationCenter__heading">
                <span className="NotificationCenter__cluster">
                  {action.cluster}
                </span>
                <span className="NotificationCenter__time">
                  {ago(action.startedAt)}
                </span>
              </div>
              <div className="NotificationCenter__action NotificationCenter__action--running">
                {LABELS[action.actionType].running}
              </div>
              <div className="NotificationCenter__title">
                {action.step ?? action.title}
              </div>
            </div>
          </li>
        ))}
        {events.map((event, index) => (
          <li
            key={event.id}
            className={`NotificationCenter__item${
              index < unread
                ? ` NotificationCenter__item--unread-${event.status}`
                : ''
            }`}
          >
            {event.status === 'success' ? (
              <CheckCircleFilled className="NotificationCenter__icon NotificationCenter__icon--success" />
            ) : (
              <CloseCircleFilled className="NotificationCenter__icon NotificationCenter__icon--failure" />
            )}
            <div className="NotificationCenter__body">
              <div className="NotificationCenter__heading">
                <span className="NotificationCenter__cluster">
                  {event.cluster}
                </span>
                <span className="NotificationCenter__time">
                  {ago(event.completedAt)}
                </span>
              </div>
              <div
                className={`NotificationCenter__action NotificationCenter__action--${event.status}`}
              >
                {LABELS[event.actionType][event.status]}
              </div>
              <div className="NotificationCenter__title">{event.title}</div>
              {event.hint && (
                <div className="NotificationCenter__hint">{event.hint}</div>
              )}
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="None of your cluster actions in the last 30 days"
      />
    );

  // The bell is personal; the whole fleet's events live on the Event Center page, for those who can open it.
  const panel = (
    <>
      {content}
      {can('audit.read') && (
        <div className="NotificationCenter__footer">
          <Button
            type="link"
            onClick={() => {
              handleOpenChange(false);
              navigate('/event-center');
            }}
          >
            View all in Event Center
          </Button>
        </div>
      )}
    </>
  );

  return (
    <Popover
      title="Notifications"
      content={panel}
      trigger="click"
      placement={placement}
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="NotificationCenter__popover"
    >
      <Button type="text" aria-label="Notifications" className={className}>
        <Badge
          count={unread}
          size="small"
          offset={[4, -2]}
          className="NotificationCenter__badge"
        >
          {running.length ? (
            <SyncOutlined spin className="NotificationCenter__bell" />
          ) : (
            <BellOutlined className="NotificationCenter__bell" />
          )}
        </Badge>
      </Button>
    </Popover>
  );
}

export default NotificationCenter;
